/**
 * Detyrat e mia — një radhë pune, jo një sistem i dytë detyrash.
 *
 * Faza 5 e dinte kush është përgjegjës për çdo detyrë onboarding-u. Ajo që
 * mungonte ishte vendi ku ai person e sheh punën e vet: IT-ja duhej të hapte
 * procesin e duhur, menaxheri po ashtu, dhe punonjësi i ri nuk kishte fare ku
 * të shikonte.
 *
 * E njëjta faqe shërben për të gjithë — HR, IT, menaxher, trajner, punonjës —
 * sepse pyetja e tyre është e njëjta: çfarë kam unë për të bërë sot.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Link } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import myWorkService, { DOCUMENT_TYPES } from '../services/myWorkService';
import onboardingService from '../services/onboardingService';
import { CATEGORIES, categoryLabel, taskMeta } from '../config/onboarding';
import usePermissions from '../hooks/usePermissions';

const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return isNaN(x) ? '—' : `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.${x.getFullYear()}`;
};

const VIEWS = [
  { code: 'open',        label: 'Të hapura' },
  { code: 'today',       label: 'Sot' },
  { code: 'week',        label: 'Këtë javë' },
  { code: 'overdue',     label: 'Të vonuara' },
  { code: 'in_progress', label: 'Në punë' },
  { code: 'done',        label: 'Të përfunduara' },
];

function Counter({ label, value, warn }) {
  return (
    <Card sx={{ flex: 1, ...(warn && value > 0 ? { borderLeft: '4px solid #D97706' } : {}) }}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: warn && value > 0 ? '#B45309' : undefined }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

export default function MyTasksPage() {
  // Punonjësi i ri e sheh detyrën e vet, por jo tërë procesin: linku te procesi
  // do t'i kthente 403 çdo herë, dhe një link që dështon gjithmonë është defekt,
  // jo mbrojtje.
  const { can } = usePermissions();
  const canOpenProcess = can('onboarding.view') || can('onboarding.manage');
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [counters, setCounters] = useState({ open: 0, today: 0, overdue: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [view, setView] = useState('open');
  const [category, setCategory] = useState('all');
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);   // { kind, task }
  const [reason, setReason] = useState('');
  const [docType, setDocType] = useState('identity_document');
  const [file, setFile] = useState(null);

  useEffect(() => { setPage(0); }, [view, category]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const [d, c] = await Promise.all([
      myWorkService().tasks({ page: page + 1, limit: rowsPerPage, view, category }),
      myWorkService().counters(),
    ]);
    if (d.error) setError(d.error); else { setRows(d.data || []); setPagination(d.pagination); }
    if (!c.error) setCounters(c);
    setLoading(false);
  }, [page, rowsPerPage, view, category]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, okText) => {
    setBusy(true); setError('');
    const r = await fn();
    setBusy(false);
    if (r?.error) { setError(r.error); return null; }
    if (okText) setFlash(okText);
    await load();
    return r;
  };

  const submitDialog = async () => {
    const t = dialog.task;
    if (dialog.kind === 'upload') {
      if (!file) return;
      const r = await run(() => myWorkService().uploadDocument(t.id, docType, file),
        'Dokumenti u ngarkua dhe pret shqyrtimin e HR-së.');
      if (r) { setDialog(null); setFile(null); }
      return;
    }
    const r = await run(() => onboardingService().setTaskStatus(t.id, dialog.kind, { reason }));
    if (r) { setDialog(null); setReason(''); }
  };

  return (
    <Box>
      <PageHeader title="Detyrat e mia" subtitle="Puna e onboarding-ut që të është caktuar ty" />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <Counter label="Të hapura" value={counters.open} />
        <Counter label="Sot" value={counters.today} />
        <Counter label="Të vonuara" value={counters.overdue} warn />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {flash && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFlash('')}>{flash}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            {VIEWS.map((v) => (
              <Chip key={v.code} label={v.label} size="small"
                color={view === v.code ? 'primary' : 'default'}
                variant={view === v.code ? 'filled' : 'outlined'}
                onClick={() => setView(v.code)} sx={{ cursor: 'pointer' }} />
            ))}
            <Box sx={{ flex: 1 }} />
            <TextField select size="small" label="Kategoria" value={category}
              onChange={(e) => setCategory(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="all">Të gjitha</MenuItem>
              {CATEGORIES.map((c) => <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>)}
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>
          ) : rows.length === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
              <AssignmentTurnedInIcon sx={{ color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                {view === 'overdue' ? 'Nuk ke detyra të vonuara.'
                  : view === 'today' ? 'Nuk ke detyra me afat sot.'
                  : view === 'done' ? 'Ende nuk ke detyra të përfunduara.'
                  : 'Nuk ke detyra aktive.'}
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1}>
              {rows.map((t) => {
                const m = taskMeta(t.status);
                const closed = ['completed', 'skipped', 'cancelled'].includes(t.status);
                return (
                  <Stack key={t.id} direction={{ xs: 'column', md: 'row' }} spacing={1}
                    alignItems={{ md: 'center' }}
                    sx={{ p: 1.25, border: '1px solid', borderColor: t.overdue ? '#F59E0B' : 'divider', borderRadius: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{t.title}</Typography>
                        <Chip size="small" label={categoryLabel(t.category)} sx={{ height: 17, fontSize: '.6rem' }} />
                        {t.required && <Chip size="small" variant="outlined" label="e detyrueshme" sx={{ height: 17, fontSize: '.6rem' }} />}
                        {t.critical && <Chip size="small" color="error" variant="outlined" label="dita 1" sx={{ height: 17, fontSize: '.6rem' }} />}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {t.employee}{t.position ? ` · ${t.position}` : ''}
                        {t.dueDate ? ` · afati ${fmtDate(t.dueDate)}` : ''}
                        {t.overdue ? ' · me vonesë' : ''}
                        {t.documentRequired ? ` · dokument (${t.acceptedDocuments} të pranuara)` : ''}
                      </Typography>
                      {t.blockedReason && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#B45309' }}>Bllokuar: {t.blockedReason}</Typography>
                      )}
                      {canOpenProcess && (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          <Link to={`/onboarding/${t.onboardingId}`} style={{ color: '#2C6E7E' }}>Hap procesin</Link>
                        </Typography>
                      )}
                    </Box>
                    <Chip size="small" color={m.color} variant="outlined" label={m.label} sx={{ height: 20, fontSize: '.66rem' }} />
                    {!closed && (
                      <Stack direction="row" spacing={0.5}>
                        {t.documentRequired && (
                          <Tooltip title="Detyra kryhet vetëm pasi HR-ja ta pranojë dokumentin">
                            <Button size="small" variant="outlined" disabled={busy}
                              startIcon={<UploadFileIcon sx={{ fontSize: '15px !important' }} />}
                              onClick={() => { setDialog({ kind: 'upload', task: t }); setFile(null); }}
                              sx={{ textTransform: 'none', fontSize: '.72rem' }}>Ngarko</Button>
                          </Tooltip>
                        )}
                        {t.status === 'pending' && (
                          <Button size="small" disabled={busy} sx={{ textTransform: 'none', fontSize: '.72rem' }}
                            onClick={() => run(() => onboardingService().setTaskStatus(t.id, 'in_progress'))}>Nis</Button>
                        )}
                        <Button size="small" disabled={busy} sx={{ textTransform: 'none', fontSize: '.72rem' }}
                          onClick={() => run(() => onboardingService().setTaskStatus(t.id, 'completed'))}>Kryer</Button>
                        <Button size="small" color="warning" disabled={busy} sx={{ textTransform: 'none', fontSize: '.72rem' }}
                          onClick={() => { setDialog({ kind: 'blocked', task: t }); setReason(''); }}>Blloko</Button>
                      </Stack>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          )}

          {pagination.total > 0 && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={Math.min(page, Math.max(0, pagination.totalPages - 1))}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Rreshta:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} nga ${count}`}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {dialog?.kind === 'upload' ? 'Ngarko dokumentin' : 'Blloko detyrën'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">{dialog?.task?.title}</Typography>
            {dialog?.kind === 'upload' ? (
              <>
                <TextField select size="small" label="Lloji i dokumentit" value={docType}
                  onChange={(e) => setDocType(e.target.value)} fullWidth>
                  {DOCUMENT_TYPES.map((d) => <MenuItem key={d.code} value={d.code}>{d.label}</MenuItem>)}
                </TextField>
                <Button variant="outlined" component="label" sx={{ textTransform: 'none' }}>
                  {file ? file.name : 'Zgjidh skedarin (PDF, JPG, PNG — deri 8 MB)'}
                  <input hidden type="file" accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </Button>
                <Alert severity="info">
                  Dokumenti shkon te HR-ja për shqyrtim. Detyra kryhet pasi ai të pranohet.
                </Alert>
              </>
            ) : (
              <TextField autoFocus multiline minRows={2} fullWidth required label="Pse është e bllokuar"
                value={reason} onChange={(e) => setReason(e.target.value)} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Anulo</Button>
          <Button variant="contained" disabled={busy || (dialog?.kind === 'upload' ? !file : !reason.trim())}
            onClick={submitDialog}>Konfirmo</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
